import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs, { type TabsProps } from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import {
  useEnableGoogleAdsConversionMapping,
  useEnableGoogleAdsIntegrationMilestone3,
} from "@/features/google-ads/useEnableGoogleAdsIntegration";
import { interlude } from "@/interlude";
import { useAuthorizeWithGoogle } from "@/pages/applications/pages/googleAdsIntegration/api/useAuthorizeWithGoogle";
import { useDeleteManagerAccount } from "@/pages/applications/pages/googleAdsIntegration/api/useDeleteManagerAccount";
import { ConversionMappingsTab } from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/ConversionMappingsTab";
import { DisconnectManagerAccountModal } from "@/pages/applications/pages/googleAdsIntegration/DisconnectManagerAccountModal";
import { ListGoogleAdsAccount } from "@/pages/applications/pages/googleAdsIntegration/ListGoogleAdsAccount";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const TabsSchema = z.enum(["general", "dataSyncSettings", "conversions"]);
type TabsSchemaType = z.infer<typeof TabsSchema>;

interface TabPanelProps extends React.ComponentProps<"div"> {
  index: TabsSchemaType;
  value: TabsSchemaType;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
  ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`google-ads-tabpanel-${index}`}
      aria-labelledby={`google-ads-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const cssLink = css`
  text-decoration: none;
`;

const cssPageTitle = css`
  margin-bottom: 0;
`;

const cssSection = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 512px;
`;

const GoogleAdsIntegrationPageInternal: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab: TabsSchemaType = location.pathname.endsWith("/conversions")
    ? TabsSchema.Enum.conversions
    : TabsSchema.Enum.general;
  const [value, setValue] = useState<TabsSchemaType>(initialTab);
  const [isDisconnectModalOpen, toggleIsDisconnectModalOpen] = useSwitch(false);

  const handleChange = useHandler<TabsProps["onChange"]>((_, newValue) => {
    const result = TabsSchema.safeParse(newValue);
    if (!result.success) return;
    setValue(result.data);
  });

  const enableMilestone3 = useEnableGoogleAdsIntegrationMilestone3();
  const enableGoogleAdsConversionMapping =
    useEnableGoogleAdsConversionMapping();
  const googleAdsManagerAccountQuery =
    interlude.googleAds.useGetManagerAccount();
  const isGoogleAdsManagerAccountConnected =
    googleAdsManagerAccountQuery.isSuccess;

  const authorizeWithGoogleMutation = useAuthorizeWithGoogle();

  const handleAuthorizeWithGoogle = useHandler(() => {
    authorizeWithGoogleMutation.mutate();
  });

  const deleteManagerAccountMutation = useDeleteManagerAccount({
    onSuccess: () => {
      enqueueSnackbar(
        t(
          "applications.googleAds.disconnectManagerAccountModal.success",
          "Google Ads disconnected",
        ),
        {
          variant: "success",
        },
      );
      toggleIsDisconnectModalOpen.off();
      navigate({ to: "/applications" });
    },
    onError: () => {
      toggleIsDisconnectModalOpen.off();
    },
  });

  const handleConfirmDisconnect = useHandler(() => {
    deleteManagerAccountMutation.mutate(undefined);
  });

  const handleAuthorize = () => {
    // TODO: Implement authorize data sync flow
  };

  return (
    <Stack gap="8px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Link to="/applications" css={cssLink}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("applications.title")}
          </Typography>
        </Link>
        <Typography variant="body" color={theme.colors.staticFgNote}>
          {t("applications.googleAds.title")}
        </Typography>
      </Breadcrumbs>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="16px"
      >
        <PageTitle css={cssPageTitle}>
          {t("applications.googleAds.title")}
        </PageTitle>
        {isGoogleAdsManagerAccountConnected ? (
          <Button
            variant="secondary"
            onClick={toggleIsDisconnectModalOpen.on}
            disabled={deleteManagerAccountMutation.isPending}
          >
            {t(
              "applications.googleAds.disconnectManagerAccountModal.button",
              "Disconnect Google Ads",
            )}
          </Button>
        ) : null}
      </Stack>
      <Tabs value={value} onChange={handleChange}>
        <Tab
          label={t("applications.googleAds.tabs.general")}
          value={TabsSchema.Values.general}
        />
        {enableMilestone3 && (
          <Tab
            label={t("applications.googleAds.tabs.dataSyncSettings")}
            value={TabsSchema.Values.dataSyncSettings}
          />
        )}
        {enableGoogleAdsConversionMapping && (
          <Tab
            label={t("applications.googleAds.tabs.conversions", "Conversions")}
            value={TabsSchema.Values.conversions}
          />
        )}
      </Tabs>
      <TabPanel value={value} index={TabsSchema.Enum.general}>
        {googleAdsManagerAccountQuery.isPending ? (
          <BarLoading />
        ) : isGoogleAdsManagerAccountConnected ? (
          <ListGoogleAdsAccount />
        ) : (
          <Stack gap="24px">
            <Stack gap="8px" css={cssSection}>
              <Typography
                variant="h3"
                fontWeight="medium"
                color={theme.colors.staticFgTitle}
              >
                {t("applications.googleAds.managerAccount.title")}
              </Typography>
              <Typography variant="note" color={theme.colors.staticFgNote}>
                {t("applications.googleAds.managerAccount.description")}
              </Typography>
              <Button
                variant="primary"
                onClick={handleAuthorizeWithGoogle}
                loading={authorizeWithGoogleMutation.isPending}
                disabled={authorizeWithGoogleMutation.isPending}
              >
                {t(
                  "applications.googleAds.oauth.authorizeButton",
                  "Connect with Google",
                )}
              </Button>
            </Stack>
            <Stack gap="8px" css={cssSection}>
              <Typography
                variant="h3"
                fontWeight="medium"
                color={theme.colors.staticFgTitle}
              >
                {t("applications.googleAds.accounts.title")}
              </Typography>
              <Typography variant="note" color={theme.colors.staticFgNote}>
                {t("applications.googleAds.accounts.description")}
              </Typography>
            </Stack>
          </Stack>
        )}
      </TabPanel>
      {enableGoogleAdsConversionMapping && (
        <TabPanel value={value} index={TabsSchema.Enum.conversions}>
          <ConversionMappingsTab />
        </TabPanel>
      )}
      <TabPanel value={value} index={TabsSchema.Enum.dataSyncSettings}>
        <Stack gap="24px">
          <Stack gap="8px" css={cssSection}>
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t(
                "applications.googleAds.dataSyncSettings.adsPerformance.title",
              )}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t(
                "applications.googleAds.dataSyncSettings.adsPerformance.description",
              )}
            </Typography>
            <Button variant="primary" onClick={handleAuthorize}>
              {t("applications.googleAds.dataSyncSettings.authorize")}
            </Button>
          </Stack>
        </Stack>
      </TabPanel>
      {isDisconnectModalOpen ? (
        <DisconnectManagerAccountModal
          open
          isLoading={deleteManagerAccountMutation.isPending}
          onClose={toggleIsDisconnectModalOpen.off}
          onConfirm={handleConfirmDisconnect}
        />
      ) : null}
    </Stack>
  );
};

export const GoogleAdsIntegrationPage: React.FC = () => {
  return (
    <PageLayout>
      <Suspense fallback={<BarLoading />}>
        <GoogleAdsIntegrationPageInternal />
      </Suspense>
    </PageLayout>
  );
};
